import { Link } from '@tanstack/react-router'
import { AlertCircle, ArrowRight, CheckCircle2, Heart, Lock, Pin, RefreshCw, ShieldCheck } from 'lucide-react'

import {
  useRefreshPinEligibility,
  type AutomationOut,
  type PinEligibilityOut,
  type PinStateOut,
  type RecentActionOut,
  type StudioSessionOut,
} from '@/api/endpoints/engagement'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { Spinner } from '@/components/ui/Spinner'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import { TimeInput } from '@/components/ui/Input'
import { Segmented } from '@/components/ui/Segmented'
import { Toggle } from '@/components/ui/Toggle'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/cn'
import { clockLabel, relativeWhen, type EngagementValues } from './model'
import { AutomationNotice, SessionNotice } from './notices'
import { Field, NumberInput, Section } from './parts'
import { pinEligibilityView, refreshResultLine } from './pinEligibility'
import styles from './ChannelEditDialog.module.scss'

export interface InteractionsTabProps {
  channelName: string
  /** Para o "Conferir agora" da verificação do canal. */
  channelSlug: string
  values: EngagementValues
  errors: Record<string, string>
  set: <K extends keyof EngagementValues>(key: K, value: EngagementValues[K]) => void
  before: (key: keyof EngagementValues) => string | undefined
  automation: AutomationOut | undefined
  session: StudioSessionOut | undefined
  pin: PinStateOut | null | undefined
  /** Opcional: o front vai ao ar antes do backend que manda o campo. */
  pinEligibility?: PinEligibilityOut | undefined
  /** O `pin_enabled` salvo, não o do formulário. */
  savedPinEnabled?: boolean
  recent: RecentActionOut[] | undefined
}

const RECENT_STATUS: Record<string, { label: string; variant: ChipVariant }> = {
  done: { label: 'Feito', variant: 'success' },
  pending: { label: 'Na fila', variant: 'warning' },
  failed: { label: 'Na fila', variant: 'warning' },
  expired: { label: 'Ignorado', variant: 'default' },
  abandoned: { label: 'Falhou', variant: 'danger' },
}

/** Aviso do canal sem a verificação do YouTube, com o "Conferir agora". */
function PinEligibilityNotice(props: { channelSlug: string; refused: boolean; canRefresh: boolean }) {
  const refresh = useRefreshPinEligibility(props.channelSlug)
  const line = refreshResultLine(refresh.data, refresh.isError ? refresh.error : null)
  return (
    <Callout
      variant="neutral"
      title="Este canal ainda não pode fixar comentários"
      action={
        props.canRefresh ? (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={refresh.isPending ? <Spinner /> : <RefreshCw />}
            aria-disabled={refresh.isPending}
            onClick={() => {
              if (!refresh.isPending) refresh.mutate()
            }}
          >
            {refresh.isPending ? 'Conferindo…' : 'Conferir agora'}
          </Button>
        ) : undefined
      }
    >
      <p>
        O YouTube só libera fixar comentário depois da verificação do canal. No YouTube Studio: Configurações → Canal →
        Qualificação de recursos → Recursos intermediários (verificação por telefone).{' '}
        {props.refused
          ? 'O Prism confere de novo sozinho 24 horas depois da recusa.'
          : 'O Prism confere de novo sozinho a cada 6 horas, ou agora pelo botão.'}
      </p>
      {props.refused ? <p>O Studio recusou o último fixado.</p> : null}
      {line && !refresh.isPending ? (
        <p className={cn(styles.refreshResult, refresh.isError && styles.refreshResultError)} role="status">
          {refresh.isError ? <AlertCircle aria-hidden /> : <CheckCircle2 aria-hidden />}
          {line}
        </p>
      ) : null}
    </Callout>
  )
}

export function InteractionsTab(props: InteractionsTabProps) {
  const { values, errors, set, before } = props
  const initial = props.channelName.trim().charAt(0).toUpperCase() || 'P'
  const pinView = pinEligibilityView(props.pinEligibility, props.savedPinEnabled ?? false)

  return (
    <div className={styles.tabPanel}>
      <SessionNotice session={props.session} />
      {props.automation && !props.automation.engagement_enabled ? (
        <div className={styles.section}>
          <AutomationNotice kind="engagement" />
        </div>
      ) : null}

      <Section>
        <div className={styles.toggleHead}>
          <Toggle
            id="ch-hearts-enabled"
            aria-label="Coração do criador"
            checked={values.hearts_enabled}
            onCheckedChange={(v) => {
              set('hearts_enabled', v)
            }}
          />
          <div className={styles.toggleHeadText}>
            <h3 className={styles.sectionTitle}>Coração do criador</h3>
            <span className={styles.hint}>O canal curte comentários com o coração de criador.</span>
          </div>
        </div>
        <div className={styles.grid2}>
          <Field
            id="ch-hearts-day"
            label="Máximo por dia"
            meta="padrão: 1"
            before={before('hearts_per_day')}
            error={errors.hearts_per_day}
            hint="Com o toggle ligado, o mínimo é 1."
          >
            <NumberInput
              id="ch-hearts-day"
              min={1}
              max={10}
              suffix="1–10"
              value={values.hearts_per_day}
              error={Boolean(errors.hearts_per_day)}
              onChange={(v) => {
                set('hearts_per_day', v ?? Number.NaN)
              }}
            />
          </Field>
          <Field
            label="Janela de horário"
            help="O robô só dá coração dentro desta janela, espalhado ao longo dela."
            before={before('hearts_start') ?? before('hearts_end')}
            error={errors.hearts_end ?? errors.hearts_start}
          >
            <div className={styles.timeRange}>
              <TimeInput
                id="ch-hearts-start"
                aria-label="Início da janela"
                value={values.hearts_start}
                onChange={(v) => {
                  set('hearts_start', v)
                }}
                error={Boolean(errors.hearts_start)}
              />
              <span className={styles.hint}>até</span>
              <TimeInput
                id="ch-hearts-end"
                aria-label="Fim da janela"
                value={values.hearts_end}
                onChange={(v) => {
                  set('hearts_end', v)
                }}
                error={Boolean(errors.hearts_end)}
              />
            </div>
          </Field>
        </div>
        <div className={styles.grid2}>
          <Field label="Comentários das últimas" before={before('hearts_window_hours')}>
            <Segmented
              aria-label="Comentários das últimas"
              options={[
                { value: '24', label: '24 h' },
                { value: '48', label: '48 h' },
                { value: '72', label: '72 h' },
              ]}
              value={String(values.hearts_window_hours)}
              onValueChange={(v) => {
                set('hearts_window_hours', Number(v) as 24 | 48 | 72)
              }}
            />
          </Field>
          <Field label="Priorizar" before={before('hearts_priority')}>
            <Segmented
              aria-label="Priorizar"
              options={[
                { value: 'questions', label: 'Perguntas' },
                { value: 'likes', label: 'Mais curtidos' },
                { value: 'recent', label: 'Mais recentes' },
              ]}
              value={values.hearts_priority}
              onValueChange={(v) => {
                set('hearts_priority', v)
              }}
            />
          </Field>
        </div>
        <div className={styles.lockRow}>
          <ShieldCheck aria-hidden />
          <div className={styles.toggleHeadText}>
            <span className={styles.lockTitle}>
              Análise de segurança <Chip variant="success">Obrigatória</Chip>
            </span>
            <span className={styles.hint}>
              Cada comentário passa antes pela análise de segurança. Comentário reprovado nunca recebe coração.
            </span>
          </div>
          <Tooltip content="Não pode ser desligada">
            <span className={styles.lockedToggle} role="img" aria-label="Análise de segurança: sempre ligada">
              <span>
                <Lock aria-hidden />
              </span>
            </span>
          </Tooltip>
        </div>
        <div className={styles.toggleHead}>
          <Toggle
            id="ch-hearts-debate"
            aria-label="Canal de debate político"
            checked={values.hearts_debate_mode}
            onCheckedChange={(v) => {
              set('hearts_debate_mode', v)
            }}
          />
          <div className={styles.toggleHeadText}>
            <span className={styles.lockTitle}>Canal de debate político</span>
            <span className={styles.hint}>
              A análise lê o roteiro do vídeo e só aprova opinião que concorda com ele. Reprova réplica ao vídeo, ironia
              com o criador e qualquer menção a voto ou candidato. Deixe desligado nos outros canais.
            </span>
          </div>
        </div>
      </Section>

      <Section>
        <div className={styles.toggleHead}>
          <Toggle
            id="ch-pin-enabled"
            aria-label="Comentar uma pergunta e fixar"
            checked={values.pin_enabled}
            disabled={pinView.disabled}
            onCheckedChange={(v) => {
              set('pin_enabled', v)
            }}
          />
          <div className={styles.toggleHeadText}>
            <h3 className={styles.sectionTitle}>Comentário fixado</h3>
            <span className={styles.hint}>
              {pinView.pausedHint && values.pin_enabled
                ? 'Pausado até a verificação: o Prism não publica a pergunta enquanto o canal não puder fixar.'
                : 'A IA gera uma pergunta na hora de fixar — usa a pergunta do roteiro quando existir.'}
            </span>
          </div>
        </div>
        {pinView.notice ? (
          <PinEligibilityNotice channelSlug={props.channelSlug} refused={pinView.refused} canRefresh={pinView.canRefresh} />
        ) : null}
        <Field label="Quando" before={before('pin_mode') ?? before('pin_delay_hours')} error={errors.pin_delay_hours}>
          <div className={styles.suffixInput}>
            <Segmented
              aria-label="Quando fixar"
              options={[
                { value: 'publish', label: 'Ao publicar' },
                { value: 'after', label: 'Horas depois' },
              ]}
              value={values.pin_mode}
              onValueChange={(v) => {
                set('pin_mode', v)
              }}
            />
            {values.pin_mode === 'after' ? (
              <NumberInput
                id="ch-pin-delay"
                aria-label="Horas depois da publicação, de 1 a 72"
                min={1}
                max={72}
                suffix="h · 1–72"
                value={values.pin_delay_hours}
                error={Boolean(errors.pin_delay_hours)}
                onChange={(v) => {
                  set('pin_delay_hours', v ?? Number.NaN)
                }}
              />
            ) : null}
          </div>
        </Field>
        <Field label="Prévia" meta={<><Chip>Exemplo</Chip> a pergunta real é gerada ao publicar</>}>
          <div className={styles.quote}>
            <span className={styles.avatar} aria-hidden>
              {initial}
            </span>
            <div className={styles.quoteBody}>
              <span className={styles.pinnedBy}>
                <Pin aria-hidden />
                Fixado por {props.channelName}
              </span>
              <span>E você, já tinha ouvido falar disso? Conta aqui nos comentários.</span>
            </div>
          </div>
        </Field>
        {props.pin ? <LastPin pin={props.pin} /> : null}
      </Section>

      <Section className={styles.soon}>
        <div className={styles.toggleHead}>
          <Toggle aria-label="Responder comentários (em breve)" checked={false} disabled />
          <div className={styles.toggleHeadText}>
            <h3 className={styles.sectionTitle}>Responder comentários</h3>
            <span className={styles.hint}>Respostas automáticas aos comentários do canal.</span>
          </div>
          <Chip variant="primary">Em breve</Chip>
        </div>
      </Section>

      <Section
        title="Últimas ações"
        meta={
          <Link to="/jobs" className={styles.jobsLink}>
            Ver em Jobs <ArrowRight aria-hidden />
          </Link>
        }
      >
        {props.recent && props.recent.length ? (
          <div>
            {props.recent.map((item) => {
              const retry = item.status === 'draft' && item.retry_at
              const status = retry
                ? { label: `Nova tentativa ${clockLabel(item.retry_at ?? '')}`, variant: 'warning' as ChipVariant }
                : (RECENT_STATUS[item.status] ?? { label: item.status, variant: 'default' as ChipVariant })
              return (
                <div key={`${item.kind}-${String(item.id)}`} className={styles.actionRow} title={item.detail || undefined}>
                  {item.kind === 'heart' ? <Heart aria-hidden /> : <Pin aria-hidden />}
                  <span className={styles.queueText}>{item.label}</span>
                  <Chip variant={status.variant}>{status.label}</Chip>
                  <span className={styles.actionWhen}>{relativeWhen(item.at)}</span>
                </div>
              )
            })}
          </div>
        ) : (
          <span className={styles.empty}>Nenhuma ação ainda.</span>
        )}
      </Section>
    </div>
  )
}

function LastPin({ pin }: { pin: PinStateOut }) {
  if (pin.status === 'draft' && pin.attempt > 0) {
    return (
      <Field label="Último fixado">
        <div className={cn(styles.quote, styles.quoteError)} role="status">
          <AlertCircle aria-hidden />
          <div className={styles.quoteBody}>
            <span className={styles.typeName}>Não foi possível gerar a pergunta — nova tentativa em 1 h</span>
            <span className={styles.hint}>
              tentativa {pin.attempt} de {pin.max_attempts} (até {pin.max_attempts} em 24 h). Se todas falharem: “Desistimos
              após {pin.max_attempts} tentativas”.
            </span>
          </div>
          <Link to="/jobs" className={styles.jobsLink}>
            Ver em Jobs
          </Link>
        </div>
      </Field>
    )
  }
  if (pin.status === 'abandoned') {
    return (
      <Field label="Último fixado">
        <div className={cn(styles.quote, styles.quoteError)} role="status">
          <AlertCircle aria-hidden />
          <div className={styles.quoteBody}>
            <span className={styles.typeName}>Desistimos após {pin.max_attempts} tentativas</span>
            <span className={styles.hint}>O vídeo fica sem comentário fixado. {pin.error}</span>
          </div>
          <Chip variant="danger">Falhou</Chip>
        </div>
      </Field>
    )
  }
  if (!pin.question) return null
  return (
    <Field label="Último fixado" meta={relativeWhen(pin.at)}>
      <div className={styles.quote}>
        <div className={styles.quoteBody}>
          <span>{pin.question}</span>
        </div>
        <Chip variant={pin.status === 'done' ? 'success' : 'warning'}>{pin.status === 'done' ? 'Feito' : 'Na fila'}</Chip>
      </div>
    </Field>
  )
}
